import { useEffect, useState } from 'react';

export const IN_PLACE_REPROCESSABLE_STATUSES = new Set([
  'PROCESS_COMPLETED',
  'PROCESS_PARTIALLY_COMPLETED',
  'PROCESS_FAILED',
  'BEDROCK_FAILS',
  'VALIDATION_FAILED',
  'VALIDATION_REJECTED',
]);

export const IN_PLACE_PROCESSING_STATUSES = new Set([
  'REPROCESS_QUEUED',
  'RECEIVED',
  'TEXT_EXTRACTION_IN_PROGRESS',
  'TEXT_EXTRACTION_DONE',
  'BEDROCK_IN_PROGRESS',
  'BEDROCK_DONE',
  'VALIDATION_IN_PROGRESS',
  'OCR_IN_PROGRESS',
]);

const pendingReprocess = new Set();
const pendingListeners = new Set();

function notifyPendingListeners() {
  pendingListeners.forEach((fn) => fn());
}

export function markReprocessPending(documentId) {
  if (!documentId) return;
  pendingReprocess.add(documentId);
  notifyPendingListeners();
}

export function clearReprocessPending(documentId) {
  if (!documentId) return;
  pendingReprocess.delete(documentId);
  notifyPendingListeners();
}

export function isReprocessPending(documentId) {
  return Boolean(documentId && pendingReprocess.has(documentId));
}

export function subscribeReprocessPending(listener) {
  pendingListeners.add(listener);
  return () => pendingListeners.delete(listener);
}

export function useReprocessPending() {
  const [, setTick] = useState(0);
  useEffect(() => subscribeReprocessPending(() => setTick((n) => n + 1)), []);
}

const GET_DOCUMENT_STATUS = `
  query DocumentReprocessStatus($id: String!) {
    getDocumentById(id: $id) {
      document_id
      status
    }
  }
`;

export const REPROCESS_DOCUMENT = `
  mutation ReprocessDocument($document_id: ID!) {
    reprocessDocument(document_id: $document_id) {
      document_id
      status
      message
      previous_status
    }
  }
`;

export function isReprocessingPipelineStatus(status) {
  return (status || '').toUpperCase() === 'REPROCESS_QUEUED';
}

export function isInPlaceReprocessableFromQueueItem(item, { canEdit = false } = {}) {
  if (!item?.doc_id || !canEdit) return false;
  if (['processing', 'reprocessing', 'queued', 'uploading', 'presigning', 'validating'].includes(item.status)) {
    return false;
  }
  if (item.status === 'duplicate') return false;
  return ['done', 'error', 'needs_review'].includes(item.status);
}

function resolvePipelineStatus(row) {
  const pipeline = (row.pipeline_status || '').toUpperCase();
  if (pipeline) return pipeline;
  const status = (row.status || '').toUpperCase();
  if (IN_PLACE_REPROCESSABLE_STATUSES.has(status) || status === 'DUPLICATE_SKIPPED') {
    return status;
  }
  if (IN_PLACE_PROCESSING_STATUSES.has(status)) return status;
  // Ficha UI status from documentFichaMapper (processed comprobante)
  if (status === 'VALIDATED') return 'PROCESS_COMPLETED';
  if (status === 'UNCLASSIFIED') return 'PROCESS_COMPLETED';
  return status;
}

export function isInPlaceReprocessable(row, { canEdit = false } = {}) {
  if (!row || !canEdit) return false;
  const docId = row.document_id || row.doc_id;
  if (isReprocessPending(docId)) return false;
  const docType = (row.doc_type || row.docType || '').toLowerCase();
  if (!docType || docType === 'hidden') return false;
  if (row.hidden_reason) return false;
  if (row.reprocess_in_progress) return false;
  const status = resolvePipelineStatus(row);
  if (!status) return false;
  if (IN_PLACE_PROCESSING_STATUSES.has(status)) return false;
  if (status === 'DUPLICATE_SKIPPED') return false;
  return IN_PLACE_REPROCESSABLE_STATUSES.has(status);
}

export function isInPlaceReprocessing(row) {
  if (!row) return false;
  const docId = row.document_id || row.doc_id;
  if (isReprocessPending(docId)) return true;
  if (row.reprocess_in_progress) return true;
  const status = resolvePipelineStatus(row);
  return IN_PLACE_PROCESSING_STATUSES.has(status);
}

export function isDocumentReprocessing(row) {
  if (!row) return isReprocessPending(row);
  const docId = row.document_id || row.doc_id;
  if (isReprocessPending(docId)) return true;
  return isInPlaceReprocessing(row);
}

export async function reprocessDocumentInPlace(callApi, documentId) {
  const data = await callApi(REPROCESS_DOCUMENT, { document_id: documentId });
  return data?.reprocessDocument;
}

export function inPlaceReprocessToastMessage(result) {
  if (result?.status === 'QUEUED') {
    return 'Reproceso iniciado. Se volverá a extraer la información del archivo.';
  }
  return result?.message || 'No se pudo iniciar el reproceso';
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export async function pollDocumentAfterReprocess(
  callApi,
  documentId,
  { intervalMs = 3000, maxMs = 180000, onTick } = {},
) {
  const start = Date.now();
  while (Date.now() - start < maxMs) {
    const data = await callApi(GET_DOCUMENT_STATUS, { id: documentId });
    const doc = data?.getDocumentById;
    onTick?.(doc);
    const status = (doc?.status || '').toUpperCase();
    const inProgress = IN_PLACE_PROCESSING_STATUSES.has(status)
      || Boolean(doc?.reprocess_in_progress);
    if (!inProgress) {
      return doc;
    }
    await sleep(intervalMs);
  }
  throw new Error('Tiempo de espera agotado mientras se reprocesa el documento');
}

export async function runInPlaceReprocess(callApi, documentId, {
  onQueued,
  onTick,
  onComplete,
} = {}) {
  markReprocessPending(documentId);
  try {
    const result = await reprocessDocumentInPlace(callApi, documentId);
    if (result?.status !== 'QUEUED') {
      clearReprocessPending(documentId);
      return result;
    }
    onQueued?.(result);
    const doc = await pollDocumentAfterReprocess(callApi, documentId, { onTick });
    clearReprocessPending(documentId);
    onComplete?.(doc);
    return { ...result, document: doc };
  } catch (err) {
    clearReprocessPending(documentId);
    throw err;
  }
}
